import { App, Form, InputNumber } from "antd";
import type { TableProps } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CustomButton,
  CustomInput,
  CustomSelect,
  CustomSwitch,
  CustomTable,
} from "sipli-fleet-core/components";
import {
  useMutationUpdateVehicleDetails,
  useQueryDeviceDetailDrivers,
  useQueryDeviceDetailsLookup,
} from "src/queries/deviceDetails.queries";
import { useQueryGetClientLookup } from "src/queries/lookup.queries";
import {
  useQueryGetUnitManagementVehicleMakeLookup,
  useQueryGetUnitManagementVehicleModelLookup,
  useQueryGetUnitManagementVehicleTypeLookup,
} from "src/queries/unitManagement.queries";
import type { FuelSensorDetail, VehicleDetail } from "src/services/deviceDetailsService";
import { getApiErrorMessage } from "src/util/apiError";
import DetailsCard, { DetailsReadOnlyField } from "./DetailsCard";
import {
  formatDate,
  haveFormValuesChanged,
  lookupValues,
  toOptions,
  valueId,
} from "./details.helpers";
import { useEpochFormatter } from "../../../../../../sipli-fleet-core/components/EpochDate/useEpochFormatter";

interface VehicleFormValues {
  licensePlate?: string;
  vin?: string;
  makeId?: string | number;
  modelId?: string | number;
  vehicleTypeId?: string | number;
  grossWeight?: number;
  iconText?: string;
  odometer?: number;
  driverId?: string | number;
  registrationCountry?: string | number;
  certRegistrationNo?: string;
  customerId?: string | number;
  withTpms?: boolean;
  notificationByReminder?: boolean;
}

const getVehicleFormValues = (vehicle: VehicleDetail): VehicleFormValues => ({
  licensePlate: vehicle.licensePlate ?? "",
  vin: vehicle.vin ?? "",
  makeId: valueId(vehicle.make),
  modelId: valueId(vehicle.model),
  vehicleTypeId: valueId(vehicle.vehicleType),
  grossWeight: vehicle.grossWeight,
  iconText: vehicle.iconText ?? "",
  odometer: vehicle.odometer === undefined ? undefined : Math.floor(vehicle.odometer),
  driverId: valueId(vehicle.driver),
  registrationCountry:
    vehicle.registrationCountry?.code || valueId(vehicle.registrationCountry) || undefined,
  certRegistrationNo: vehicle.certRegistrationNo ?? "",
  customerId: valueId(vehicle.customer),
  withTpms: Boolean(vehicle.withTpms),
  notificationByReminder: Boolean(vehicle.notificationByReminder),
});

const VehicleDetailsCard = ({
  vehicle,
  fuelSensors,
}: {
  vehicle: VehicleDetail;
  fuelSensors?: FuelSensorDetail[];
}) => {
  const { t, i18n } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm<VehicleFormValues>();
  const [initialValues, setInitialValues] = useState(() => getVehicleFormValues(vehicle));
  const [makeId, setMakeId] = useState<string | number | undefined>(valueId(vehicle.make));
  const mutation = useMutationUpdateVehicleDetails();
  const makeQuery = useQueryGetUnitManagementVehicleMakeLookup();
  const modelQuery = useQueryGetUnitManagementVehicleModelLookup(makeId);
  const typeQuery = useQueryGetUnitManagementVehicleTypeLookup();
  const clientQuery = useQueryGetClientLookup();
  const countriesQuery = useQueryDeviceDetailsLookup("countries");
  const driversQuery = useQueryDeviceDetailDrivers(valueId(vehicle.customer));
  const withTpms = Form.useWatch("withTpms", form);

  const formatEpoch = useEpochFormatter();

  useEffect(() => {
    const values = getVehicleFormValues(vehicle);
    setInitialValues(values);
    form.resetFields();
    form.setFieldsValue(values);
    setMakeId(valueId(vehicle.make));
  }, [form, vehicle]);

  const handleCancel = () => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    setMakeId(initialValues.makeId);
  };

  const makeValues = lookupValues(makeQuery.data);
  const modelValues = lookupValues(modelQuery.data);
  const typeValues = lookupValues(typeQuery.data);
  const clientValues = lookupValues(clientQuery.data);

  const handleSave = async () => {
    if (vehicle.id === undefined) return;
    const values = await form.validateFields();
    try {
      await mutation.mutateAsync({
        id: vehicle.id,
        payload: {
          modelId: values.modelId,
          licensePlate: values.licensePlate,
          vin: values.vin,
          withTpms: Boolean(values.withTpms),
          notificationByReminder: values.withTpms ? Boolean(values.notificationByReminder) : false,
          odometer: values.odometer,
          iconText: values.iconText,
          vehicleTypeId: values.vehicleTypeId,
          customerId: values.customerId ?? "",
          trPresetId: values.withTpms ? (vehicle.trPresetId ?? null) : null,
          grossWeight: values.grossWeight,
          driverId: values.driverId ?? null,
          registrationCountry: values.registrationCountry,
          certRegistrationNo: values.certRegistrationNo,
          freeText: vehicle.freeText,
          contentTypeId: vehicle.contentTypeId ?? null,
        },
      });
      setInitialValues(values);
      message.success(t("vehicle_detail_updated_successfully"));
    } catch (error) {
      message.error(`Error: ${getApiErrorMessage(error) ?? t("vehicle_detail_update_failed")}`);
    }
  };

  const sensorColumns: TableProps<FuelSensorDetail>["columns"] = useMemo(
    () => [
      { title: t("serial1"), key: "serial", render: (_, item) => item.unit?.serial || "--" },
      { title: t("brand"), key: "brand", render: (_, item) => item.unit?.make?.name || "--" },
      { title: t("model1"), key: "model", render: (_, item) => item.unit?.model?.name || "--" },
    ],
    [t]
  );
  const vehicleSummary = [
    vehicle.make?.name,
    vehicle.model?.name,
    vehicle.licensePlate || vehicle.vin,
  ]
    .filter(Boolean)
    .join(" ");

  const registrationDate = vehicle.registrationDate ? formatEpoch(vehicle.registrationDate) : null;
  const defaultCarYear = i18n.language === "it" ? "04/17/2024" : "2024-17-04";

  return (
    <DetailsCard title={t("vehicle_details")} extra={vehicleSummary || "--"}>
      <Form form={form} layout="vertical" onFinish={handleSave} requiredMark={false}>
        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 xl:grid-cols-3">
          <Form.Item name="licensePlate" label={t("license_plate")}>
            <CustomInput className="common-input" placeholder={t("license_plate")} />
          </Form.Item>
          <Form.Item name="vin" label={t("vin")}>
            <CustomInput className="common-input" placeholder={t("vin")} />
          </Form.Item>
          <Form.Item
            name="makeId"
            label={t("brand")}
            rules={[{ required: true, message: t("brand_required") }]}
          >
            <CustomSelect
              className="common-select"
              placeholder={t("brand")}
              showSearch
              optionFilterProp="label"
              options={toOptions(makeValues)}
              loading={makeQuery.isLoading}
              onChange={(value) => {
                setMakeId(value);
                form.setFieldValue("modelId", undefined);
              }}
            />
          </Form.Item>
          <Form.Item name="vehicleTypeId" label={t("vehicle_type")}>
            <CustomSelect
              className="common-select"
              placeholder={t("vehicle_type")}
              showSearch
              optionFilterProp="label"
              options={toOptions(typeValues)}
              loading={typeQuery.isLoading}
            />
          </Form.Item>
          <Form.Item name="iconText" label={t("icon_text")}>
            <CustomInput className="common-input" placeholder={t("icon_text")} />
          </Form.Item>
          <Form.Item
            name="modelId"
            label={t("model1")}
            rules={[{ required: true, message: t("vehicle_model_required") }]}
          >
            <CustomSelect
              className="common-select"
              placeholder={t("model1")}
              showSearch
              optionFilterProp="label"
              options={toOptions(modelValues)}
              loading={modelQuery.isLoading}
              disabled={!makeId}
            />
          </Form.Item>
          <DetailsReadOnlyField label={t("depot")} value={vehicle.depot} />
          <Form.Item
            name="grossWeight"
            label={t("gross_weight")}
            rules={[{ type: "number", min: 0, message: t("gross_weight_positive") }]}
          >
            <InputNumber
              className="common-input"
              min={0}
              style={{ width: "100%" }}
              placeholder={t("gross_weight")}
            />
          </Form.Item>
          <Form.Item
            name="odometer"
            label={t("odometer_label")}
            rules={[
              { required: true, message: t("odometer_required") },
              { type: "number", min: 0, message: t("odometer_min") },
            ]}
          >
            <InputNumber
              className="common-input"
              min={0}
              step={1}
              style={{ width: "100%" }}
              placeholder={t("odometer_label")}
            />
          </Form.Item>
          <DetailsReadOnlyField
            label={t("car_year")}
            value={vehicle.carYear ?? registrationDate ?? defaultCarYear}
          />
          <Form.Item name="driverId" label={t("driver")}>
            <CustomSelect
              className="common-select"
              placeholder={t("driver")}
              allowClear
              showSearch
              optionFilterProp="label"
              options={toOptions(
                driversQuery.data?.map((item) => ({
                  ...item,
                  name: [item.name, item.surname].filter(Boolean).join(" "),
                }))
              )}
              loading={driversQuery.isLoading}
            />
          </Form.Item>
          <Form.Item name="customerId" label={t("company")}>
            <CustomSelect
              className="common-select"
              placeholder={t("company")}
              showSearch
              optionFilterProp="label"
              options={toOptions(clientValues)}
              loading={clientQuery.isLoading}
            />
          </Form.Item>
        </div>

        <div className="mt-5 mb-5 border-t text-[15.75px] leading-[20px] border-gray4 pt-5 font-semibold text-black">
          {t("registration")}
        </div>
        <div className="grid grid-cols-1 gap-x-4 md:grid-cols-3">
          <DetailsReadOnlyField
            label={t("date_of_first_registration")}
            value={formatDate(vehicle.registrationDate)}
          />
          <Form.Item name="registrationCountry" label={t("registration_country")}>
            <CustomSelect
              className="common-select"
              placeholder={t("registration_country")}
              allowClear
              showSearch
              optionFilterProp="label"
              options={toOptions(
                countriesQuery.data?.map((item) => ({
                  ...item,
                  id: item.code || item.id,
                  value: item.code || item.value,
                }))
              )}
              loading={countriesQuery.isLoading}
            />
          </Form.Item>
          <div>
            <label className="block text-sm mb-2 mt-0.5">{t("registration_certificate_no")}</label>
            <Form.Item name="certRegistrationNo">
              <CustomInput
                className="common-input"
                placeholder={t("registration_certificate_no")}
              />
            </Form.Item>
          </div>
        </div>

        <div className="my-4 border-y border-gray4 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="mb-2 block text-[15.75px] font-semibold leading-[20px]">
                {t("vehicle_visibility_in_tpms")}
              </span>
              <p className="mb-3 text-[14px] font-normal leading-[18px] text-gray9">
                {t(
                  "enable_to_make_the_vehicle_visible_in_tpms_and_select_a_preset_from_the_list_to_start_the_configuration"
                )}
              </p>
            </div>
            <Form.Item name="withTpms" valuePropName="checked" className="mb-0! shrink-0">
              <CustomSwitch />
            </Form.Item>
          </div>
          {withTpms && (
            <div className="flex items-center justify-between gap-4">
              <span className="text-[15.75px] font-semibold leading-[20px]">
                {t("notification_by_reminder")}
              </span>
              <Form.Item
                name="notificationByReminder"
                valuePropName="checked"
                className="mb-0! shrink-0"
              >
                <CustomSwitch aria-label={t("notification_by_reminder")} />
              </Form.Item>
            </div>
          )}
        </div>

        <div className="mb-4 text-[15.75px] font-semibold leading-[20px] text-black">
          {t("fuel_sensors")}
        </div>
        <CustomTable<FuelSensorDetail>
          customClass="customers-table fuel-sensors-table"
          data={fuelSensors ?? []}
          columns={sensorColumns}
          rowKey={(item) => String(item.id ?? item.unit?.id ?? item.unit?.serial)}
          showSearchInput={false}
          showCustomizeColumns={false}
          showDownload={false}
          isShowPagination={false}
          locale={{ emptyText: t("no_data") }}
        />
        <div className="flex justify-end gap-2 pt-10">
          <CustomButton
            type="secondary"
            label={t("cancel")}
            className="!h-[32px] !w-[119px] !rounded-[4px] !text-[14px] !font-semibold !leading-[18px] !tracking-[0.05px]"
            disabled={mutation.isPending}
            onClick={handleCancel}
          />
          <Form.Item noStyle shouldUpdate>
            {() => (
              <CustomButton
                htmlType="submit"
                type="primary"
                label={t("save")}
                className="!h-[32px] !w-[119px] !rounded-[4px] !text-[14px] !font-semibold !leading-[18px] !tracking-[0.05px] !text-gray2"
                loading={mutation.isPending}
                disabled={
                  !haveFormValuesChanged(initialValues, form.getFieldsValue(true)) ||
                  mutation.isPending
                }
              />
            )}
          </Form.Item>
        </div>
      </Form>
    </DetailsCard>
  );
};

export default VehicleDetailsCard;
